import { useTRPC } from "@nwga/features/client";
import { Button } from "@nwga/ui/components/button";
import { Skeleton } from "@nwga/ui/components/skeleton";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { ShieldCheckIcon } from "lucide-react";
import { toast } from "sonner";
import { AuthCard } from "../components/auth-card.tsx";

/** Public landing page for a platform-administrator invitation. */
export const Route = createFileRoute("/admin-invitations/$token")({
	component: AcceptAdminInvitationPage,
});

function AcceptAdminInvitationPage() {
	const { token } = Route.useParams();
	const trpc = useTRPC();
	const queryClient = useQueryClient();
	const navigate = useNavigate();
	const invitation = useQuery(
		trpc.admin.adminInvitations.preview.queryOptions({ token }),
	);
	const session = useQuery(trpc.session.queryOptions());
	const accept = useMutation(
		trpc.admin.adminInvitations.accept.mutationOptions({
			onSuccess: async () => {
				// The platform role and every permission derived from it changed.
				await queryClient.invalidateQueries();
				toast.success("You're now an administrator");
				await navigate({ to: "/admin/users" });
			},
			onError: (error) => toast.error(error.message),
		}),
	);

	if (invitation.isPending || session.isPending || session.data === undefined) {
		return (
			<AuthCard title="Checking your invitation…">
				<Skeleton className="h-24 w-full" />
			</AuthCard>
		);
	}

	if (invitation.isError) {
		return (
			<AuthCard
				title="We can't find that invitation"
				description="The link may have been mistyped, withdrawn, or replaced."
			>
				<Button asChild className="w-full">
					<Link to="/">Go to NWGA Connect</Link>
				</Button>
			</AuthCard>
		);
	}

	const { email, state } = invitation.data;
	const user = session.data.user;

	if (state !== "pending") {
		const explanation = {
			accepted: "This invitation has already been accepted.",
			revoked: "An administrator withdrew this invitation.",
			expired:
				"This invitation has expired. Ask an administrator for a new one.",
		}[state];

		return (
			<AuthCard
				title="This invitation is no longer usable"
				description={explanation}
			>
				<Button asChild className="w-full">
					<Link to={user === null ? "/sign-in" : "/signed-in"}>
						{user === null ? "Sign in" : "Continue"}
					</Link>
				</Button>
			</AuthCard>
		);
	}

	if (user === null) {
		return (
			<AuthCard
				title="Become an administrator"
				description="You've been invited to help administer NWGA Connect."
				footer={
					<>
						Already have an account?{" "}
						<Link
							to="/sign-in"
							search={{ redirect: `/admin-invitations/${token}` }}
							className="font-medium text-foreground underline-offset-4 hover:underline"
						>
							Sign in
						</Link>
					</>
				}
			>
				<div className="flex flex-col gap-4">
					<AdminInvitationSummary email={email} />
					<Button asChild className="w-full">
						<Link
							to="/sign-up"
							search={{ redirect: `/admin-invitations/${token}` }}
						>
							Create an account to accept
						</Link>
					</Button>
				</div>
			</AuthCard>
		);
	}

	const wrongAccount =
		user.email.trim().toLowerCase() !== email.trim().toLowerCase();
	if (wrongAccount) {
		return (
			<AuthCard
				title="This invitation is for a different address"
				description={`It was sent to ${email}, but you're signed in as ${user.email}.`}
			>
				<div className="flex flex-col gap-3">
					<AdminInvitationSummary email={email} />
					<p className="text-muted-foreground text-sm">
						Sign out and sign back in as {email} to accept it.
					</p>
					<Button asChild variant="outline" className="w-full">
						<Link to="/signed-in">Continue with this account</Link>
					</Button>
				</div>
			</AuthCard>
		);
	}

	return (
		<AuthCard
			title="Become an administrator"
			description="Accepting gives you full access to accounts, business reviews, moderation, and platform settings."
		>
			<div className="flex flex-col gap-4">
				<AdminInvitationSummary email={email} />
				<Button
					className="w-full"
					disabled={accept.isPending}
					onClick={() => accept.mutate({ token })}
				>
					{accept.isPending ? "Accepting…" : "Accept administrator role"}
				</Button>
				<Button asChild variant="ghost" className="w-full">
					<Link to="/">No thanks</Link>
				</Button>
			</div>
		</AuthCard>
	);
}

function AdminInvitationSummary({ email }: { readonly email: string }) {
	return (
		<div className="flex items-start gap-3 rounded-lg border border-dashed p-4">
			<ShieldCheckIcon
				className="mt-0.5 size-5 shrink-0 text-muted-foreground"
				aria-hidden="true"
			/>
			<dl className="min-w-0 text-sm">
				<div className="flex gap-2">
					<dt className="text-muted-foreground">Role</dt>
					<dd className="font-medium">Administrator</dd>
				</div>
				<div className="flex gap-2">
					<dt className="text-muted-foreground">Invited</dt>
					<dd className="truncate font-medium">{email}</dd>
				</div>
			</dl>
		</div>
	);
}
